<script setup lang="ts">
/**
 * The bout screen — a full-viewport fight, not a panel popup.
 *
 * Layout follows the design mockup: docks backdrop, hero standing LEFT facing
 * right, opponent RIGHT facing left, both full-body, and the six numbered
 * action cards along the bottom. The HUD (Tekken-style HP bars left/right and
 * the centre timer) is ours — the mockup did not draw it.
 *
 * Drop-in for the old TacticalBattle popup: same props in, same `done` event
 * out, so CityMap / Arena / Dungeons mount it unchanged.
 */
import { ref, reactive, onMounted, onUnmounted, computed, watch } from 'vue';
import {
  startFight, startGarrison, startHeist, startArena, fightRound, fightHeal, fightAbandon, continueHeist,
  type FightView, type MobAction,
} from '../game/mobfight-api';
import { fightPose, fightCard, FIGHT_BG, HP_BAR, mobArt, SHOP_ART, itemThumb, type FightPose } from '../game/art';
import { poseFit } from '../game/fight-pose-metrics';
import { RARITY_HEX } from '../game/item-math';
import { CLASS_LABELS, type HeroClass } from '../game/hero-api';
import * as sfx from '../game/sfx';
import { confirmAction } from '../game/confirm';
import cashIcon from '../assets/omerta/icons/cash.webp';

const props = defineProps<{
  col?: number; row?: number; heroClass?: string;
  garrisonId?: string; heistId?: string; arena?: boolean;
  /**
   * A bout the caller has ALREADY started. Pass this whenever you can: opening
   * the fight is what validates nerve, level and the spatial gate, so a caller
   * that starts it first can show "not enough nerve" in its own panel instead of
   * throwing the player onto a fight screen just to read an error. Without it we
   * fall back to starting on mount (which resumes a live session, never opens a
   * second one).
   */
  initial?: FightView | null;
}>();
const emit = defineEmits<{ (e: 'done', changed: boolean): void }>();

const view = ref<FightView | null>(null);
const error = ref('');
const busy = ref(false);
const changed = ref(false); // did the world change (a kill happened)

// ── Card → action. The number and title are baked into the card art, so this
// order is fixed and must match it: 1 STRIKE · 2 HEAVY · 3 GUARD · 4 FLEE ·
// 5 <class ability> · 6 PATCH UP (the heal endpoint, not a round action).
const CARDS = [
  { n: 1, action: 'strike' as MobAction },
  { n: 2, action: 'heavy' as MobAction },
  { n: 3, action: 'guard' as MobAction },
  { n: 4, action: 'flee' as MobAction },
  { n: 5, action: 'ability' as MobAction },
  { n: 6, action: 'heal' as const },
];

// ── Poses ──
// Pacing. Poses are single stills, so a swap is a hard cut unless it is given
// time and a blend — at 620ms with an instant swap the fight read as a strobe.
// POSE_HOLD_MS is how long an action pose stays up; the crossfade itself is in
// the .fs-pose transition below. Both are deliberately unhurried: the bout is
// turn-based, nothing is waiting on them.
const POSE_HOLD_MS = 900;
const heroPose = ref<FightPose>('default');
const enemyPose = ref<FightPose>('default');
// One timer PER FIGHTER. A single shared timer meant the second flash of a round
// (hero swings, enemy reels) cancelled the first fighter's reset and left them
// stuck mid-pose.
const poseTimers: Record<'hero' | 'enemy', number> = { hero: 0, enemy: 0 };
function flash(who: 'hero' | 'enemy', pose: FightPose, ms = POSE_HOLD_MS) {
  const r = who === 'hero' ? heroPose : enemyPose;
  r.value = pose;
  window.clearTimeout(poseTimers[who]);
  poseTimers[who] = window.setTimeout(() => { if (r.value === pose) r.value = 'default'; }, ms);
}
const ACTION_POSE: Record<string, FightPose> = {
  strike: 'strike', heavy: 'heavy', guard: 'guard', flee: 'flee', ability: 'ability', heal: 'patch-up',
};

const heroClass = computed(() => view.value?.hero.class ?? props.heroClass);
const heroImg = computed(() => fightPose(heroClass.value, heroPose.value));
// The plate shows the in-game trade, never the raw engine enum ("MAGE" → "Fixer")
const heroClassLabel = computed(() => {
  const c = heroClass.value?.toUpperCase() as HeroClass | undefined;
  return (c && CLASS_LABELS[c]) || 'You';
});

/**
 * Warm every pose frame for a combatant the moment the fight opens. The stage
 * swaps keyed <img>s per pose; without this, the FIRST use of each pose on a
 * slow connection fetches mid-crossfade — the fighter blanks out and pops back
 * in, which reads as the sprite "jumping" between stances (mobile QA report).
 */
const ALL_POSES: FightPose[] = [
  'default', 'strike', 'heavy', 'guard', 'flee',
  'ability', 'patch-up', 'damage', 'damage-2', 'win', 'defeat',
];
const warmedPoseSets = new Set<string>();
function preloadPoses(cls?: string | null) {
  const key = cls?.toUpperCase();
  if (!key || warmedPoseSets.has(key)) return;
  warmedPoseSets.add(key);
  for (const pose of ALL_POSES) {
    const url = fightPose(key, pose);
    if (url) new Image().src = url;
  }
}
watch(
  () => [heroClass.value, view.value?.enemy.archetype] as const,
  ([hero, enemy]) => { preloadPoses(hero); preloadPoses(enemy); },
  { immediate: true },
);

/**
 * The opponent. `enemy.archetype` is the ONE key that covers every fight kind —
 * the Pit sends the bot's hero class lowercased (warrior|paladin|archer|mage),
 * and mobs, garrisons and heists all send a balance archetype
 * (thug|goon|bruiser|hitman|legbreaker|kingpin). `fight/` now holds a pose set
 * for all ten, so the opponent animates everywhere, not just in the Pit.
 * mobArt() stays as the fallback for anything without a pose set.
 */
/** Does this opponent have a pose set at all? Drives the fallback framing —
 *  a posed fighter stands on the ground like the hero, a portrait gets framed. */
const enemyIsFighter = computed(() => !!fightPose(view.value?.enemy.archetype, 'default'));
const enemyImg = computed(() => {
  const v = view.value;
  if (!v) return undefined;
  return fightPose(v.enemy.archetype, enemyPose.value)
      ?? mobArt(v.enemy.type, v.enemy.archetype);
});

/**
 * Fighter-image sizing (QA: hero rendered bigger than the opponent, feet at
 * different heights). The pose stills are independently generated cutouts —
 * each bakes in its own transparent margin — so a flat `height: 100%` scales
 * that baked-in padding right along with the character. `poseFit` measures
 * (offline, see fight-pose-metrics.ts) how much of each frame is actually the
 * character vs. padding, and these computed styles use that to make both
 * fighters' SILHOUETTES the same height and stand on the same ground line,
 * regardless of the raw art's inconsistent canvases. Only meaningful for a
 * posed fighter — the portrait-fallback mob (.is-mob) keeps its own framing
 * (height/object-fit set in CSS), so its style binding is left undefined.
 */
const heroFit = computed(() => poseFit(heroClass.value, heroPose.value));
const enemyFit = computed(() => (enemyIsFighter.value ? poseFit(view.value?.enemy.archetype, enemyPose.value) : null));

/**
 * HP bars are driven by ANIMATED refs, not the raw view. A round arrives from the
 * server fully resolved (both fighters' final HP in one response), but showing
 * that at once reads as "I punched us both". So `play()` steps these two numbers
 * through the round — hero swings, enemy drops; enemy swings, hero drops — and the
 * bars (bound here) follow. They resync to the view whenever the view changes for
 * any other reason (mount, heal, resume, room advance).
 */
const animHeroHp = ref(0);
const animEnemyHp = ref(0);
const heroMaxHp = computed(() => view.value?.hero.maxHp ?? 1);
const enemyMaxHp = computed(() => view.value?.enemy.maxHp ?? 1);
const heroPct = computed(() => Math.max(0, Math.min(100, animHeroHp.value / heroMaxHp.value * 100)));
const enemyPct = computed(() => Math.max(0, Math.min(100, animEnemyHp.value / enemyMaxHp.value * 100)));
const heroHpText = computed(() => `${Math.max(0, Math.round(animHeroHp.value))}/${heroMaxHp.value}`);
const enemyHpText = computed(() => `${Math.max(0, Math.round(animEnemyHp.value))}/${enemyMaxHp.value}`);
const heroLow = computed(() => heroPct.value <= 25);
const enemyLow = computed(() => enemyPct.value <= 25);

/** Snap the bars to the view — used when HP changes outside a stepped round. */
function syncBars() {
  animHeroHp.value = view.value?.hero.hp ?? 0;
  animEnemyHp.value = view.value?.enemy.hp ?? 0;
}
const delay = (ms: number) => new Promise<void>((r) => window.setTimeout(r, ms));

const ended = computed(() => !!view.value && !view.value.active);

/** Heists are multi-room and garrisons are multi-defender: `wave` is "which of
 *  how many", and a heist parks on `awaitingNext` between rooms until the player
 *  presses on. Without this a heist dead-ends after the first room. */
const wave = computed(() => view.value?.wave ?? null);
const awaitingNext = computed(() => !!view.value?.heist?.awaitingNext);
const banked = computed(() => view.value?.heist?.bankedCash ?? 0);

async function pressOn() {
  if (busy.value) return;
  busy.value = true;
  try {
    const prevRoom = view.value?.wave?.index ?? 0;
    view.value = await continueHeist();
    syncBars();
    if ((view.value.wave?.index ?? 0) > prevRoom) sfx.clash();
    heroPose.value = 'default';
    enemyPose.value = 'default';
  } catch (e) {
    error.value = (e as Error).message;
  } finally {
    busy.value = false;
  }
}

// ── Cosmetic fight timer. Counts elapsed time and NOTHING else: it never ends
// the bout, never touches the outcome, never calls the server (explicit design
// decision — a Tekken-style timeout was rejected).
const seconds = ref(0);
let tick = 0;
const clock = computed(() => `${String(Math.floor(seconds.value / 60)).padStart(2, '0')}:${String(seconds.value % 60).padStart(2, '0')}`);

// ── The session's REAL clock: the server keeps an idle bout alive for 5 minutes
// (refreshed on every action). Before this, letting it lapse dead-ended the
// screen — every move answered "No active fight" and even fleeing was
// impossible. Now the deadline is visible, and lapsing closes the bout cleanly.
const expiresIn = ref(0);
const cold = ref(false);
const expiryWarn = computed(() => !cold.value && !ended.value && expiresIn.value > 0 && expiresIn.value <= 60);
const expiryClock = computed(() => `${Math.floor(expiresIn.value / 60)}:${String(expiresIn.value % 60).padStart(2, '0')}`);
watch(view, (v) => { if (v?.expiresInSec !== undefined) expiresIn.value = v.expiresInSec; });
/** A server "that fight is gone" reply, on any action → cold end, not an error. */
function goCold() {
  cold.value = true;
  error.value = '';
  heroPose.value = 'default';
  enemyPose.value = 'default';
}

// ── Floating damage popups. 2s on screen — at 1.1s the number was gone before
// the eye got to it (player feedback), and the round pacing gives each popup
// its own beat anyway so longer never overlaps the next one. ──
const POP_MS = 2000;
let pid = 0;
const popups = reactive<{ id: number; side: 'hero' | 'enemy'; text: string; kind: string }[]>([]);
function pop(side: 'hero' | 'enemy', text: string, kind = 'dmg') {
  const id = ++pid;
  popups.push({ id, side, text, kind });
  window.setTimeout(() => { const i = popups.findIndex((p) => p.id === id); if (i >= 0) popups.splice(i, 1); }, POP_MS);
}

function cardEnabled(n: number): boolean {
  const v = view.value;
  // between heist rooms there is nobody to swing at — the only move is "press on"
  if (!v || busy.value || ended.value || awaitingNext.value || cold.value) return false;
  if (n === 5) return v.ability.ready;
  // patch-up: needs a heal left, room to heal, and the cash for it. healAffordable
  // already folds all three together (see the view) — greying the card beats
  // clicking into an "Insufficient balance" error.
  if (n === 6) return v.healAffordable ?? v.healsLeft > 0;
  return true;
}

async function play(card: { n: number; action: MobAction | 'heal' }) {
  if (!cardEnabled(card.n)) return;
  busy.value = true;
  error.value = '';
  try {
    if (card.action === 'heal') {
      const res = await fightHeal();
      view.value = res;
      flash('hero', 'patch-up');
      // the heal is a single, non-combat event — snap the hero bar up to it
      animHeroHp.value = res.hero.hp;
      sfx.heal();
      pop('hero', 'PATCHED', 'heal');
      return;
    }

    flash('hero', ACTION_POSE[card.action] ?? 'strike');
    const res = await fightRound(card.action); // fully-resolved round
    // a completed non-arena bout persisted hero HP — the world/profile changed
    if (res.result) changed.value = true;
    await animateRound(res);
    // commit the authoritative state only after the beat-by-beat has played out
    view.value = res;
    syncBars();
  } catch (e) {
    const msg = (e as Error).message;
    // idle TTL lapsed server-side → the bout is simply over, not an error state
    if (/no active fight/i.test(msg)) goCold();
    else error.value = msg;
  } finally {
    busy.value = false;
  }
}

/**
 * Walk one round's outcome through the two bars so cause and effect read in
 * order: (bleed) → hero acts, enemy reels → enemy retaliates, hero reels. The
 * server already resolved everything; we only pace the presentation, driving the
 * anim refs off the outcome deltas and letting the bar's own CSS tween each drop.
 */
async function animateRound(res: FightView) {
  const o = res.outcome;
  if (!o) return;
  const before = view.value; // pre-round HP baseline
  animHeroHp.value = before?.hero.hp ?? res.hero.hp;
  animEnemyHp.value = before?.enemy.hp ?? res.enemy.hp;

  // MAGE bleed ticks at the top of the round, before anyone swings
  if (o.bleedDmg > 0) {
    await delay(200);
    animEnemyHp.value = Math.max(0, animEnemyHp.value - o.bleedDmg);
    pop('enemy', `-${o.bleedDmg}`, 'dmg');
    flash('enemy', 'damage');
    await delay(420);
  }

  // STEP A — the hero's blow lands (or misses)
  await delay(300); // let the swing pose read before the hit
  if (o.heroHeal > 0) { // PALADIN Second Wind self-patch
    animHeroHp.value = Math.min(heroMaxHp.value, animHeroHp.value + o.heroHeal);
    pop('hero', `+${o.heroHeal}`, 'heal');
  }
  if (o.heroDmg > 0) {
    animEnemyHp.value = Math.max(0, animEnemyHp.value - o.heroDmg);
    pop('enemy', `-${o.heroDmg}`, o.heroEvent === 'crit' ? 'crit' : 'dmg');
    flash('enemy', 'damage');
    sfx.hit();
  } else if (o.heroEvent === 'miss') {
    pop('enemy', 'MISS', 'miss');
  }

  // STEP B — the enemy answers, in its own beat, only if it can
  if (o.mobDmg > 0) {
    await delay(560);
    flash('enemy', 'strike');
    await delay(260);
    animHeroHp.value = Math.max(0, animHeroHp.value - o.mobDmg);
    pop('hero', `-${o.mobDmg}`, o.mobEvent === 'crit' ? 'crit' : 'dmg');
    flash('hero', 'damage');
    sfx.hit();
  } else if (o.mobEvent === 'block') {
    await delay(300);
    pop('hero', 'BLOCK', 'miss');
  } else if (o.fleeFailed) {
    pop('hero', 'BLOCKED IN', 'miss');
  }

  await delay(320); // hold on the final frame before the outcome panel
}

// End-of-bout poses: the winner celebrates, the loser goes down. Watched rather
// than set at each call site so a bout that ends on ANY path — a landed hit, a
// failed flee, the opponent's swing — settles the same way. A fled bout is a
// no-contest: nobody poses.
watch(view, (v) => {
  if (!v || v.active || !v.result) return;
  window.clearTimeout(poseTimers.hero);
  window.clearTimeout(poseTimers.enemy);
  if (v.fled) {
    // a walk-out is a no-contest — hold the hero mid-escape on the outcome
    // screen instead of snapping back to idle (the pose timer would).
    heroPose.value = 'flee';
    enemyPose.value = 'default';
    return;
  }
  const won = v.result === 'VICTORY';
  heroPose.value = won ? 'win' : 'defeat';
  enemyPose.value = won ? 'defeat' : 'win';
});

function leave() {
  emit('done', changed.value);
}

/**
 * Walk out. For MOB/GARRISON/HEIST this is unchanged — an immediate, no-
 * questions bail. For an ARENA bout (the Pit), walking out mid-fight is a
 * FORFEIT — it settles exactly like losing the last round (rating drop +
 * stake burn) — so we confirm first (this can't be undone) and then show the
 * settled LOSS outcome instead of just vanishing.
 */
async function bail() {
  if (busy.value) return;
  if (!ended.value) {
    if (view.value?.kind === 'arena') {
      const go = await confirmAction({
        kicker: 'The Pit',
        title: 'Walk out of the bout?',
        note: 'Leaving mid-fight is a forfeit — it counts as a loss, same as getting dropped. Rating and stake settle accordingly.',
        confirmLabel: 'Walk out (forfeit)',
        cancelLabel: 'Keep fighting',
        danger: true,
      });
      if (!go) return;
    }
    busy.value = true;
    try {
      const res = await fightAbandon();
      if (res.result) {
        // Arena forfeit — a settled view came back; show the loss outcome
        // (the end-of-bout panel below) instead of leaving immediately. The
        // watch(view, ...) pose effect and the "Leave"/top-bar button both
        // already handle an ended view correctly from here.
        view.value = res as FightView;
        syncBars();
        changed.value = true; // rating/gold changed — the profile header should refresh
        return;
      }
    } catch { /* ignore */ } finally {
      busy.value = false;
    }
  }
  leave();
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') { void bail(); return; }
  const n = Number(e.key);
  if (n >= 1 && n <= 6) { const c = CARDS.find((x) => x.n === n); if (c) void play(c); }
}

onMounted(async () => {
  if (props.initial) {
    // Caller already started (and therefore validated) the bout — nothing to do.
    view.value = props.initial;
    syncBars();
  } else {
    try {
      // Fallback for callers that mount us cold. NOTE: every start-* resumes an
      // already-live session rather than opening a second one — starting a fresh
      // bout here would charge the player nerve twice.
      view.value = props.garrisonId
        ? await startGarrison(props.garrisonId)
        : props.heistId
          ? await startHeist(props.heistId)
          : props.arena
            ? await startArena()
            : await startFight(props.col ?? 0, props.row ?? 0);
      syncBars();
    } catch (e) {
      error.value = (e as Error).message;
    }
  }
  tick = window.setInterval(() => {
    if (ended.value || cold.value) return;
    seconds.value += 1;
    if (expiresIn.value > 0) {
      expiresIn.value -= 1;
      if (expiresIn.value === 0) goCold(); // lapsed while we watched
    }
  }, 1000);
  window.addEventListener('keydown', onKeydown);
});
onUnmounted(() => {
  window.clearInterval(tick);
  window.clearTimeout(poseTimers.hero);
  window.clearTimeout(poseTimers.enemy);
  window.removeEventListener('keydown', onKeydown);
});

</script>

<template>
  <div class="fs-root" :style="{ backgroundImage: `url(${FIGHT_BG})` }">
    <div class="fs-scrim"></div>

    <!-- ── HUD: HP bars left/right + the cosmetic timer between them ── -->
    <header class="fs-hud">
      <div class="fs-bar fs-bar-home">
        <span class="fs-name">{{ heroClassLabel }}</span>
        <div class="fs-track">
          <img :src="HP_BAR.frame" alt="" class="fs-frame" />
          <div class="fs-groove">
            <div class="fs-fill is-hero" :class="{ 'is-low': heroLow }" :style="{ width: heroPct + '%' }"></div>
          </div>
          <span class="fs-hp">{{ heroHpText }}</span>
        </div>
      </div>

      <div class="fs-clock" role="timer" aria-label="Fight time">
        <span class="fs-clock-t">{{ clock }}</span>
        <span class="fs-clock-r">Round {{ view?.round ?? 1 }}</span>
        <!-- the session's real deadline, surfaced only when it gets close -->
        <span v-if="expiryWarn" class="fs-clock-x">Goes cold in {{ expiryClock }}</span>
        <!-- heists run several rooms, garrisons several defenders -->
        <span v-if="wave" class="fs-clock-w">
          {{ view?.kind === 'heist' ? 'Room' : 'Defender' }} {{ wave.index }}/{{ wave.total }}
        </span>
        <span v-if="banked > 0" class="fs-clock-b">Banked {{ banked.toLocaleString() }}</span>
      </div>

      <div class="fs-bar fs-bar-guest">
        <span class="fs-name fs-name-r">{{ view?.enemy.name ?? '—' }}</span>
        <div class="fs-track is-guest">
          <img :src="HP_BAR.frame" alt="" class="fs-frame" />
          <div class="fs-groove">
            <div class="fs-fill is-enemy" :class="{ 'is-low': enemyLow }" :style="{ width: enemyPct + '%' }"></div>
          </div>
          <span class="fs-hp">{{ enemyHpText }}</span>
        </div>
      </div>

      <button class="fs-bail btn-deco btn-deco--ghost btn-deco--sm" :disabled="busy" @click="bail">
        {{ ended ? 'Leave' : 'Walk out' }}
      </button>
    </header>

    <!-- ── The two fighters ── -->
    <div class="fs-stage">
      <div class="fs-fighter fs-hero">
        <Transition name="fs-pose">
          <img
            v-if="heroImg" :key="heroPose" :src="heroImg" :alt="heroClassLabel"
            :style="{ height: heroFit.height, transform: `translateY(${heroFit.translateY}%)` }"
          />
        </Transition>
        <div class="fs-pops">
          <span v-for="p in popups.filter((x) => x.side === 'hero')" :key="p.id" :class="['fs-pop', `is-${p.kind}`]">{{ p.text }}</span>
        </div>
      </div>

      <div class="fs-fighter fs-enemy" :class="{ 'is-mob': !enemyIsFighter }">
        <Transition name="fs-pose">
          <img
            v-if="enemyImg" :key="enemyPose" :src="enemyImg" :alt="view?.enemy.name"
            :style="enemyFit ? { height: enemyFit.height, transform: `scaleX(-1) translateY(${enemyFit.translateY}%)` } : undefined"
          />
        </Transition>
        <div class="fs-pops">
          <span v-for="p in popups.filter((x) => x.side === 'enemy')" :key="p.id" :class="['fs-pop', `is-${p.kind}`]">{{ p.text }}</span>
        </div>
      </div>
    </div>

    <p v-if="error" class="fs-error">{{ error }}</p>

    <!-- ── Outcome ── -->
    <div v-if="ended" class="fs-outcome corner-frame">
      <span class="corner-bracket corner-tl"></span><span class="corner-bracket corner-tr"></span>
      <span class="corner-bracket corner-bl"></span><span class="corner-bracket corner-br"></span>
      <h2>{{ view?.fled ? 'You walked out' : view?.result === 'VICTORY' ? 'You win' : 'Beaten' }}</h2>

      <div v-if="view?.rewards?.leveledUp" class="fs-oc-levelup">
        <span>&#9670;</span> Level up — Level {{ view.rewards.level }} <span>&#9670;</span>
      </div>

      <!-- the Pit settles in ELO + cash stakes; without this the bout has no point -->
      <div v-if="view?.kind === 'arena' && view.arena" class="fs-plates">
        <div class="fs-plate">
          <span class="fs-plate-art fs-badge-elo" :class="view.arena.won ? 'is-win' : 'is-loss'">{{ view.arena.won ? '▲' : '▼' }}</span>
          <div>
            <div class="fs-plate-label">Rating</div>
            <div class="fs-plate-num" :class="{ 'is-loss': !view.arena.won }">
              {{ view.arena.ratingChange >= 0 ? '+' : '−' }}{{ Math.abs(view.arena.ratingChange) }} &rarr; {{ view.arena.rating }}
            </div>
            <div class="fs-plate-sub">{{ view.arena.title }}</div>
          </div>
        </div>
        <div class="fs-plate">
          <div>
            <div class="fs-plate-label">Record</div>
            <div class="fs-plate-num">{{ view.arena.wins }}W&ndash;{{ view.arena.losses }}L</div>
            <div v-if="view.arena.streak !== 0" class="fs-plate-sub">
              {{ view.arena.streak > 0 ? `${view.arena.streak}-win streak` : `${Math.abs(view.arena.streak)}-loss skid` }}
            </div>
          </div>
        </div>
        <div v-if="view.arena.goldWon > 0 || view.arena.goldLost > 0" class="fs-plate">
          <img :src="SHOP_ART.cash_rush" alt="" class="fs-plate-art" />
          <div>
            <div class="fs-plate-label">{{ view.arena.won ? 'Lifted off him' : 'Paid out' }}</div>
            <div class="fs-plate-num" :class="{ 'is-loss': !view.arena.won }">
              {{ view.arena.won ? `+${view.arena.goldWon.toLocaleString()}` : `−${view.arena.goldLost.toLocaleString()}` }}
            </div>
          </div>
        </div>
      </div>

      <!-- the take -->
      <div v-if="view?.rewards || (view?.kind === 'heist' && view.heist)" class="fs-plates">
        <div v-if="view?.rewards && view.rewards.cash > 0" class="fs-plate">
          <img :src="SHOP_ART.cash_rush" alt="" class="fs-plate-art" />
          <div>
            <div class="fs-plate-label">The take</div>
            <div class="fs-plate-num">+{{ view.rewards.cash.toLocaleString() }}</div>
          </div>
        </div>
        <div v-if="view?.rewards && view.rewards.xp > 0" class="fs-plate">
          <img :src="SHOP_ART.xp_booster" alt="" class="fs-plate-art" />
          <div>
            <div class="fs-plate-label">Reputation</div>
            <div class="fs-plate-num is-xp">+{{ view.rewards.xp.toLocaleString() }} XP</div>
          </div>
        </div>
        <div v-if="view?.kind === 'heist' && view.heist" class="fs-plate">
          <img :src="SHOP_ART.chest_pull" alt="" class="fs-plate-art" />
          <div>
            <div class="fs-plate-label">Banked from the job</div>
            <div class="fs-plate-num">{{ view.heist.bankedCash.toLocaleString() }}</div>
          </div>
        </div>
      </div>

      <!-- loot: the real piece, framed in its rarity -->
      <div v-if="view?.loot" class="fs-loot" :style="{ '--rar': RARITY_HEX[view.loot.rarity] }">
        <div class="fs-loot-art"><img :src="itemThumb(view.loot.type, view.loot.rarity, view.loot.name)" :alt="view.loot.name" /></div>
        <div>
          <div class="fs-plate-label">Lifted off the body</div>
          <div class="fs-loot-name">{{ view.loot.name }}</div>
          <span class="fs-rarity">{{ view.loot.rarity.toLowerCase() }}</span>
        </div>
      </div>

      <button class="btn-deco btn-deco--gold btn-deco--lg" @click="leave">
        {{ view?.result === 'VICTORY' ? 'Collect and go' : 'Walk it off' }}
      </button>
    </div>

    <!-- The session lapsed while the player was away — a clean no-contest end
         instead of every card answering "No active fight". -->
    <div v-else-if="cold" class="fs-outcome corner-frame">
      <span class="corner-bracket corner-tl"></span><span class="corner-bracket corner-tr"></span>
      <span class="corner-bracket corner-bl"></span><span class="corner-bracket corner-br"></span>
      <h2>The fight went cold</h2>
      <p class="fs-next">Nobody threw a punch for five minutes — both crews drifted off. No blood, no take.</p>
      <button class="btn-deco btn-deco--gold btn-deco--lg" @click="leave">Walk away</button>
    </div>

    <!-- Between heist rooms: the only available move. -->
    <div v-else-if="awaitingNext" class="fs-outcome corner-frame">
      <span class="corner-bracket corner-tl"></span><span class="corner-bracket corner-tr"></span>
      <span class="corner-bracket corner-bl"></span><span class="corner-bracket corner-br"></span>
      <h2>Room clear</h2>
      <p class="fs-next">
        {{ view?.heist?.nextEnemyName ? `Next up: ${view.heist.nextEnemyName}` : 'The way ahead is open.' }}
        <span v-if="banked > 0"> · {{ banked.toLocaleString() }} banked</span>
      </p>
      <div class="fs-next-row">
        <button class="btn-deco btn-deco--gold" :disabled="busy" @click="pressOn">Press on</button>
        <button class="btn-deco btn-deco--ghost" :disabled="busy" @click="bail">Take the take</button>
      </div>
    </div>

    <!-- ── The six action cards. Art carries its own number + title. ── -->
    <footer v-else-if="!awaitingNext" class="fs-cards">
      <button
        v-for="c in CARDS" :key="c.n"
        class="fs-card"
        :class="{ 'is-off': !cardEnabled(c.n) }"
        :disabled="!cardEnabled(c.n)"
        @click="play(c)"
      >
        <img :src="fightCard(heroClass, c.n)" :alt="`Action ${c.n}`" />
        <span v-if="c.n === 5 && view && !view.ability.ready" class="fs-badge">Cooling</span>
        <span v-else-if="c.n === 6 && view" class="fs-badge" :class="{ 'is-broke': view.healAffordable === false && view.healsLeft > 0 }">
          {{ view.healsLeft }} left · <img :src="cashIcon" alt="cash" class="fs-badge-cash" />{{ view.healCost ?? 150 }}
        </span>
        <span class="fs-key">{{ c.n }}</span>
      </button>
    </footer>
  </div>
</template>

<style scoped>
.fs-root {
  position: fixed; inset: 0; z-index: 60;
  display: flex; flex-direction: column;
  background-size: cover; background-position: center;
  overflow: hidden;
}
/* keeps the fighters readable against the lamps + wet cobbles */
.fs-scrim {
  position: absolute; inset: 0; pointer-events: none;
  background:
    radial-gradient(120% 80% at 50% 40%, transparent 40%, rgba(6, 4, 10, 0.55)),
    linear-gradient(180deg, rgba(6, 4, 10, 0.72) 0%, transparent 26%, transparent 52%, rgba(6, 4, 10, 0.88) 100%);
}

/* ── HUD ── */
.fs-hud {
  position: relative; z-index: 2;
  display: grid; grid-template-columns: 1fr auto 1fr auto;
  align-items: start; gap: 12px;
  padding: 14px clamp(10px, 2vw, 26px) 0;
}
/* Ornate art-deco HP bars: ONE generated gold frame (no portrait circle, no
   scratch texture — player feedback) shared by both sides, guest mirrored. The
   frame's interior groove is empty in the art; `.fs-groove` sits exactly in it
   (measured insets, see art.ts HP_BAR.groove) and holds the plain CSS fill that
   does the actual depleting — red for the hero, steel-blue for the enemy, each
   draining away from its own fighter, Tekken style. Count reads off the bar. */
.fs-bar {
  position: relative; display: flex; flex-direction: column; gap: 4px; max-width: 500px;
  /* A flex item's default min-width is `auto` (its content's min-content size),
     not 0 — without this override an unusually long enemy name (a name plate
     is arbitrary player/mob text, unbounded) can force this grid column WIDER
     than the space actually available, instead of letting .fs-name's own
     ellipsis (below) do its job. See the mobile .fs-track fix for the OTHER,
     bigger version of this same class of bug. */
  min-width: 0;
}
.fs-bar-home { margin-right: auto; align-items: flex-start; }
.fs-bar-guest { margin-left: auto; align-items: flex-end; }
.fs-name {
  font-family: var(--font-label, sans-serif); font-size: 10px; letter-spacing: 0.18em;
  text-transform: uppercase; color: var(--color-gold); padding: 0 4px;
  /* Never let a long name (either fighter's) push wider than its own bar —
     truncate instead of overflowing into (or past) the opposite side. */
  display: block; max-width: 100%; min-width: 0;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.fs-track {
  position: relative; width: min(42vw, 480px);
  aspect-ratio: 1414 / 140; /* the frame art's own ratio — never stretched */
}
.fs-frame {
  position: absolute; inset: 0; width: 100%; height: 100%;
  pointer-events: none; z-index: 2; /* the frame overhangs the groove edges */
  filter: drop-shadow(0 3px 8px rgba(0, 0, 0, 0.6));
}
.fs-track.is-guest .fs-frame { transform: scaleX(-1); }
/* the measured empty channel inside the frame art */
.fs-groove {
  position: absolute;
  left: 10.11%; right: 9.9%; top: 20%; bottom: 20.71%;
  background: linear-gradient(180deg, #060409, #0e0a12);
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.9);
  overflow: hidden; z-index: 1;
}
.fs-fill {
  position: absolute; top: 0; bottom: 0; left: 0;
  transition: width 420ms cubic-bezier(0.4, 0, 0.2, 1), background 300ms ease;
}
/* guest drains the other way — anchor its fill to the right edge */
.fs-track.is-guest .fs-fill { left: auto; right: 0; }
.fs-fill.is-hero {
  background: linear-gradient(180deg, #ff6a5a 0%, #d0271b 55%, #8f140c 100%);
  box-shadow: inset 0 1px 0 rgba(255, 200, 190, 0.4);
}
.fs-fill.is-enemy {
  background: linear-gradient(180deg, #6ab8ff 0%, #2f7fe0 55%, #1a4f9c 100%);
  box-shadow: inset 0 1px 0 rgba(200, 225, 255, 0.4);
}
/* the last sliver of health pulses so a near-death read is unmistakable */
.fs-fill.is-low { animation: fs-low 0.9s ease-in-out infinite; }
@keyframes fs-low { 50% { filter: brightness(1.5); } }
/* the count, read straight off the bar (above the frame overhang) */
.fs-hp {
  position: absolute; inset: 0; z-index: 3;
  display: grid; place-items: center;
  font-family: var(--font-heading, serif); font-variant-numeric: tabular-nums;
  font-size: clamp(12px, 1.5vh, 15px); letter-spacing: 0.02em;
  color: #fff; text-shadow: 0 1px 2px rgba(0, 0, 0, 0.95), 0 0 6px rgba(0, 0, 0, 0.8);
  pointer-events: none;
}

.fs-clock {
  display: flex; flex-direction: column; align-items: center; gap: 2px;
  padding: 6px 18px; min-width: 108px;
  background: linear-gradient(180deg, rgba(20, 16, 12, 0.9), rgba(8, 6, 5, 0.95));
  border: 1px solid color-mix(in srgb, var(--color-gold) 40%, transparent);
  clip-path: polygon(7px 0, calc(100% - 7px) 0, 100% 7px, 100% calc(100% - 7px),
                     calc(100% - 7px) 100%, 7px 100%, 0 calc(100% - 7px), 0 7px);
}
.fs-clock-t { font-family: var(--font-heading, serif); font-size: 22px; line-height: 1; color: var(--color-gold-bright); }
.fs-clock-r { font-family: var(--font-label, sans-serif); font-size: 9px; letter-spacing: 0.2em; text-transform: uppercase; color: var(--color-text-dim); }

.fs-bail { align-self: start; }

/* ── Fighters ── */
.fs-stage { position: relative; z-index: 1; flex: 1; min-height: 0; display: flex; align-items: flex-end; justify-content: space-between; padding: 0 clamp(8px, 5vw, 90px); }
.fs-fighter { position: relative; height: 78%; display: flex; align-items: flex-end; }
.fs-fighter img { height: 100%; width: auto; object-fit: contain; filter: drop-shadow(0 18px 26px rgba(0, 0, 0, 0.75)); }
.fs-enemy img { transform: scaleX(-1); }
/* mobs have no pose set — a portrait, framed so it reads as deliberate */
.fs-enemy.is-mob img {
  height: 62%; align-self: center; border: 1px solid color-mix(in srgb, var(--color-gold) 35%, transparent);
  transform: none; object-fit: cover;
}

/* Crossfade between pose stills — the leaving frame is taken out of flow so the
   two overlap instead of the box collapsing mid-swap. */
.fs-pose-enter-active, .fs-pose-leave-active { transition: opacity 260ms ease; }
.fs-pose-enter-from, .fs-pose-leave-to { opacity: 0; }
.fs-pose-leave-active { position: absolute; bottom: 0; }

.fs-pops { position: absolute; inset: 0; pointer-events: none; }
.fs-pop {
  position: absolute; top: 18%; left: 50%;
  font-family: var(--font-heading, serif); font-size: 28px; font-weight: 700;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.9);
  /* rise fast, then HOLD readable, then fade — keep in step with POP_MS */
  animation: fs-pop 2s cubic-bezier(0.16, 0.8, 0.4, 1) forwards;
}
.fs-pop.is-dmg { color: #ff7a6a; }
.fs-pop.is-crit { color: #ffd66a; font-size: 34px; }
.fs-pop.is-miss { color: #d8cfba; font-size: 21px; letter-spacing: 0.08em; }
.fs-pop.is-heal { color: #7fd08a; }
@keyframes fs-pop {
  0%   { transform: translate(-50%, 0); opacity: 0; }
  8%   { transform: translate(-50%, -18px); opacity: 1; }
  72%  { transform: translate(-50%, -44px); opacity: 1; }
  100% { transform: translate(-50%, -64px); opacity: 0; }
}

.fs-error {
  position: absolute; bottom: 30%; left: 50%; transform: translateX(-50%); z-index: 5;
  padding: 8px 14px; background: rgba(10, 6, 6, 0.9);
  border: 1px solid color-mix(in srgb, var(--color-error) 50%, transparent);
  color: var(--color-error); font-size: 13px;
}

/* ── Outcome ── */
.fs-outcome {
  position: relative; z-index: 3; margin: 0 auto 26px; padding: 20px 34px;
  display: flex; flex-direction: column; align-items: center; gap: 14px;
  background: #0c0f14;
  border: 1px solid var(--color-border); border-radius: 12px;
}
/* Outcome: the take is shown as iconed brass plates, the loot as the real piece
   framed in its rarity. No banner art — it was visual overload on top of the
   screen's own backdrop. */
.fs-oc-levelup {
  font-family: var(--font-label, sans-serif); font-size: 11px; letter-spacing: 0.18em;
  text-transform: uppercase; color: var(--color-gold-bright);
}
.fs-plates { display: flex; flex-wrap: wrap; justify-content: center; gap: 10px; }
.fs-plate {
  display: flex; align-items: center; gap: 9px; padding: 8px 14px; text-align: left;
  background: #0c0f14; /* == the icons' own baked backdrop, so they dissolve into it */
  border: 1px solid color-mix(in srgb, var(--color-gold) 30%, transparent);
  clip-path: polygon(5px 0, calc(100% - 5px) 0, 100% 5px, 100% calc(100% - 5px),
                     calc(100% - 5px) 100%, 5px 100%, 0 calc(100% - 5px), 0 5px);
}
.fs-plate-art { width: 26px; height: 26px; object-fit: contain; flex: none; }
.fs-badge-elo { display: grid; place-items: center; font-size: 15px; }
.fs-badge-elo.is-win { color: var(--color-gold-bright); }
.fs-badge-elo.is-loss { color: #d98a8a; }
.fs-plate-label {
  font-family: var(--font-label, sans-serif); font-size: 9px; letter-spacing: 0.16em;
  text-transform: uppercase; color: var(--color-text-dim);
}
.fs-plate-num { font-family: var(--font-heading, serif); font-size: 17px; color: var(--color-gold); line-height: 1.2; }
.fs-plate-num.is-loss { color: #d98a8a; }
.fs-plate-num.is-xp { color: #9fd8a8; }
.fs-plate-sub { font-family: var(--font-body, serif); font-size: 11px; color: var(--color-text-dim); }
.fs-loot {
  display: flex; align-items: center; gap: 12px; padding: 10px 16px; text-align: left;
  border: 1px solid color-mix(in srgb, var(--rar, #888) 55%, transparent);
  background: #0c0f14; /* rarity stays on the border + glow; a wash here would
                          re-expose the icon's own square */
  box-shadow: 0 0 18px color-mix(in srgb, var(--rar, #888) 22%, transparent);
}
.fs-loot-art { width: 46px; height: 46px; flex: none; }
.fs-loot-art img { width: 100%; height: 100%; object-fit: contain; }
.fs-loot-name { font-family: var(--font-heading, serif); font-size: 16px; color: var(--rar, var(--color-gold)); }
.fs-rarity {
  font-family: var(--font-label, sans-serif); font-size: 9px; letter-spacing: 0.16em;
  text-transform: uppercase; color: var(--rar, var(--color-text-dim));
}
.fs-next { font-family: var(--font-body, serif); font-size: 14px; color: var(--color-text-dim); text-align: center; }
.fs-next-row { display: flex; gap: 10px; flex-wrap: wrap; justify-content: center; }
.fs-clock-w, .fs-clock-b {
  font-family: var(--font-label, sans-serif); font-size: 9px; letter-spacing: 0.14em;
  text-transform: uppercase; color: var(--color-gold);
}
.fs-clock-x {
  font-family: var(--font-label, sans-serif); font-size: 9px; letter-spacing: 0.12em;
  text-transform: uppercase; color: #e8907f;
  animation: fs-low 1.2s ease-in-out infinite;
}
.fs-clock-b { color: #9fd8a8; }
.fs-outcome h2 { font-family: var(--font-heading, serif); font-size: 26px; color: var(--color-gold); text-transform: uppercase; letter-spacing: 0.04em; }

/* ── Cards ── */
.fs-cards {
  position: relative; z-index: 3;
  display: flex; justify-content: center; align-items: flex-end;
  gap: clamp(4px, 0.8vw, 12px);
  /* headroom: overflow-x:auto clips VERTICALLY too, so without top padding the
     hover/press lift (-10px) sliced the top off whichever card was under the
     cursor. 16px covers the lift + focus ring. */
  padding: 16px 10px 14px;
  overflow-x: auto; scrollbar-width: none;
}
.fs-cards::-webkit-scrollbar { display: none; }
.fs-card {
  position: relative; flex: 0 0 auto; padding: 0; border: 0; background: transparent;
  cursor: pointer; line-height: 0;
  transition: transform 160ms ease, filter 160ms ease;
}
.fs-card > img { height: clamp(104px, 17vh, 178px); width: auto; display: block; }
.fs-card:not(.is-off):hover { transform: translateY(-10px); filter: drop-shadow(0 10px 20px rgba(233, 193, 102, 0.3)); }
.fs-card:not(.is-off):active { transform: translateY(-3px); }
.fs-card.is-off { cursor: not-allowed; filter: grayscale(0.85) brightness(0.45); }
.fs-badge {
  position: absolute; left: 50%; bottom: 6%; transform: translateX(-50%);
  padding: 2px 7px; white-space: nowrap;
  display: inline-flex; align-items: center; gap: 3px;
  background: rgba(8, 6, 5, 0.9); border: 1px solid color-mix(in srgb, var(--color-gold) 45%, transparent);
  font-family: var(--font-label, sans-serif); font-size: 9px; letter-spacing: 0.08em;
  color: var(--color-gold); line-height: 1.4;
}
/* can't afford the patch-up: the card is already greyed by is-off, this tints
   the price red so the reason is legible rather than a mystery */
.fs-badge.is-broke { color: #e8907f; border-color: color-mix(in srgb, var(--color-error) 55%, transparent); }
/* PATCH UP charges CASH, not gold bars (QA assumed gold since the shop's
   patch-up costs bars) — an explicit icon next to the number removes the
   ambiguity instead of a bare number. */
.fs-badge-cash { width: 9px; height: 9px; object-fit: contain; flex: none; }
.fs-key {
  position: absolute; top: 4%; right: 6%;
  font-family: var(--font-label, sans-serif); font-size: 9px; color: var(--color-text-dim);
  opacity: 0.6;
}

/* ── Phone ──────────────────────────────────────────────────────────────────
   At 390px the desktop layout simply does not fit: the two bars alone are 383px
   (before the clock or Walk-out), and six cards at their desktop height come to
   ~624px. So on a phone the cards are sized from the VIEWPORT — six always fit
   across, no sideways scroll — and the HUD drops to two rows so the bars keep
   their ratio instead of being squashed. */
@media (max-width: 700px) {
  .fs-hud {
    grid-template-columns: 1fr auto 1fr;   /* Walk-out leaves the row… */
    gap: 6px; padding: 8px 8px 0;
  }
  /* BUG (QA): `min(46vw, 240px)` was sized as a fraction of the FULL viewport,
     not of the space actually left after the clock column + gaps + hud
     padding — at 390px-wide that's 179px per side, and 2×179 + the clock
     (~64px) + 2 gaps (6px) + hud padding (16px) = ~451px, ~61px WIDER than
     the 390px screen. The overflow wasn't clipped evenly; the whole guest
     (enemy) column — bar, HP text, and the enemy's name plate — was shoved
     that far past the right edge, entirely off-screen (measured: the name's
     own box started at x=391 on a 390px viewport, i.e. 0% of it was visible).
     Fixed formula: solve directly for "half of what's left after the clock's
     floor width + gaps + padding" instead of a bare viewport fraction, so it
     fits BY CONSTRUCTION at any phone width, not just the one this got
     eyeballed against. A small clock-width margin (60 vs the clock's
     measured ~64px) absorbs a wider clock (heist "Banked X" / wave text can
     run longer than the plain timer); max(84px, …) keeps the bar from
     collapsing to nothing on the very narrowest phones. */
  .fs-track { width: max(84px, min(calc(50vw - 60px), 240px)); }
  .fs-name { font-size: 8px; }
  .fs-hp { font-size: 11px; }
  .fs-clock { min-width: 64px; padding: 3px 8px; }
  .fs-clock-t { font-size: 15px; }
  .fs-clock-r, .fs-clock-w, .fs-clock-b { font-size: 7px; letter-spacing: 0.1em; }
  /* …and becomes a compact plate pinned clear of both bars */
  .fs-bail { position: absolute; top: 46px; right: 8px; z-index: 5; }

  .fs-stage { padding: 0 2px; }
  .fs-fighter { height: 66%; }
  /* the taller two-row card tray below eats the frame's bottom — lift the
     backdrop's focal point so the street scene rides above the cards, and the
     bottom-anchored fighters rise with the shorter stage (QA: "push the
     characters and the background higher") */
  .fs-root { background-position: center 28%; }

  /* QA: action cards were 6-across slivers (~60px). Now a 3×2 grid — each
     card roughly doubles; order stays 1-2-3 / 4-5-6 so PATCH UP ends
     bottom-right. justify-items centers the art in its cell. */
  .fs-cards {
    display: grid; grid-template-columns: repeat(3, 1fr);
    justify-items: center; align-items: end;
    gap: 6px 4px; padding: 4px 8px 10px; overflow-x: visible;
  }
  .fs-card { flex: none; width: 100%; display: flex; justify-content: center; }
  .fs-card > img { height: auto; width: min(100%, calc((100vw - 24px) / 3)); }
  .fs-card:not(.is-off):hover { transform: none; filter: none; }  /* no hover on touch */
  .fs-badge { font-size: 9px; padding: 2px 5px; bottom: 4%; gap: 3px; }
  .fs-badge-cash { width: 9px; height: 9px; }
  .fs-key { display: none; }                /* no keyboard to hint at */

  .fs-outcome { margin: 0 8px 10px; padding: 14px 16px; }
  .fs-plate { padding: 6px 10px; gap: 6px; }
  .fs-plate-num { font-size: 14px; }
  .fs-plate-art { width: 20px; height: 20px; }
}

/* Landscape phone: almost no height. Give it to the fighters. */
@media (max-height: 480px) and (orientation: landscape) {
  .fs-track { width: min(30vw, 200px); }
  .fs-fighter { height: 72%; }
  .fs-card > img { height: 15vh; width: auto; }
  .fs-cards { padding-bottom: 4px; }
}
@media (prefers-reduced-motion: reduce) {
  .fs-card, .fs-fill { transition: none; }
  .fs-fill.is-low { animation: none; }
  .fs-pose-enter-active, .fs-pose-leave-active { transition: none; }
  .fs-pop { animation-duration: 0.01ms; }
}
</style>
